import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { SkeletonCard } from '@/components/ui/SkeletonLoader'
import { BRAND_MINT } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import {
  getAdminOrganizationsApi,
  getAdminOrganizationByIdApi,
  updateAdminOrganizationApi,
  updateOrgVerificationStatusApi,
  deleteAdminOrganizationApi,
  type AdminOrganization,
  type AdminOrganizationDetail,
} from '@/lib/api'
import {
  getOrgSubscriptionApi,
  updateOrgSubscriptionApi,
  type AdminOrgSubscription,
} from '@/lib/billing'
import {
  LoaderIcon,
  BuildingIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  HashIcon,
  MapPinIcon,
  GlobeIcon,
  ClockIcon,
  UsersIcon,
  BanknoteIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon,
  ActivityIcon,
  FileEditIcon,
  Trash2Icon,
  TagIcon,
  TrendingUpIcon,
} from 'lucide-react'

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-destructive/10 text-destructive' },
  inactive: { label: 'Inactive', color: 'bg-muted text-muted-foreground' },
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

function formatTimestamp(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export const Route = createFileRoute('/dashboard/admin/organizations')({
  component: () => {
    const [orgs, setOrgs] = useState<AdminOrganization[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [loading, setLoading] = useState(true)

    // Filters
    const [search, setSearch] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const debouncedSearchInput = useDebounce(searchInput, 300)
    const [statusFilter, setStatusFilter] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Auto-search when debounced value changes
    useEffect(() => {
      setSearch(debouncedSearchInput.trim())
      setPage(1)
    }, [debouncedSearchInput])

    // Detail drawer
    const [detailOrg, setDetailOrg] = useState<AdminOrganizationDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [showDetail, setShowDetail] = useState(false)

    // Edit org
    const [editMode, setEditMode] = useState(false)
    const [editName, setEditName] = useState('')
    const [editLegalEntityName, setEditLegalEntityName] = useState('')
    const [editVatNumber, setEditVatNumber] = useState('')
    const [editRegistrationNumber, setEditRegistrationNumber] = useState('')
    const [editDunsNumber, setEditDunsNumber] = useState('')
    const [editRegisteredAddress, setEditRegisteredAddress] = useState('')
    const [saving, setSaving] = useState(false)

    // Verification status change
    const [verifyStatus, setVerifyStatus] = useState<string>('')
    const [confirmVerify, setConfirmVerify] = useState(false)
    const [verifyLoading, setVerifyLoading] = useState(false)

    // Pricing override
    const [subscriptionData, setSubscriptionData] = useState<AdminOrgSubscription | null>(null)
    const [subLoading, setSubLoading] = useState(false)
    const [overridePrice, setOverridePrice] = useState<string>('')
    const [overrideMinimum, setOverrideMinimum] = useState<string>('')
    const [savingOverride, setSavingOverride] = useState(false)

    // Dissolve org
    const [confirmDissolve, setConfirmDissolve] = useState(false)
    const [dissolving, setDissolving] = useState(false)

    // Drawer admin search
    const [adminSearch, setAdminSearch] = useState('')

    const totalPages = Math.max(1, Math.ceil(total / limit))

    const buildParams = useCallback(() => {
      const params: Record<string, string | number> = { page, limit }
      if (search) params.search = search
      if (statusFilter) params.verified_status = statusFilter
      return params
    }, [page, limit, search, statusFilter])

    useEffect(() => {
      let cancelled = false
      async function load() {
        setLoading(true)
        try {
          const res = await getAdminOrganizationsApi(buildParams())
          if (!cancelled) {
            setOrgs(res.data)
            setTotal(res.total)
          }
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load organizations')
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      load()
      return () => { cancelled = true }
    }, [buildParams])

    const handleSearch = () => { setSearch(searchInput.trim()); setPage(1) }
    const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') handleSearch() }
    const clearFilters = () => { setSearchInput(''); setSearch(''); setStatusFilter(''); setPage(1) }
    const hasFilters = !!(search || statusFilter)

    const openDetail = async (orgId: number) => {
      setShowDetail(true)
      setDetailLoading(true)
      setEditMode(false)
      try {
        const [orgRes, subRes] = await Promise.all([
          getAdminOrganizationByIdApi(orgId),
          getOrgSubscriptionApi(orgId).catch(() => null), // subscription may not exist
        ])
        setDetailOrg(orgRes.organization)
        setEditName(orgRes.organization.org_name)
        setEditLegalEntityName(orgRes.organization.legal_entity_name || '')
        setEditVatNumber(orgRes.organization.vat_number || '')
        setEditRegistrationNumber(orgRes.organization.registration_number || '')
        setEditDunsNumber(orgRes.organization.duns_number || '')
        setEditRegisteredAddress(orgRes.organization.registered_address || '')
        setVerifyStatus(orgRes.organization.verified_status)
        if (subRes?.data) {
          setSubscriptionData(subRes.data)
          setOverridePrice(subRes.data.override_price_per_user_cents != null ? String(subRes.data.override_price_per_user_cents) : '')
          setOverrideMinimum(subRes.data.override_minimum_monthly_cents != null ? String(subRes.data.override_minimum_monthly_cents) : '')
        } else {
          setSubscriptionData(null)
          setOverridePrice('')
          setOverrideMinimum('')
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load org details')
        setShowDetail(false)
      } finally {
        setDetailLoading(false)
      }
    }

    const closeDetail = () => {
      setShowDetail(false)
      setDetailOrg(null)
      setEditMode(false)
      setConfirmVerify(false)
      setConfirmDissolve(false)
      setSubscriptionData(null)
      setOverridePrice('')
      setOverrideMinimum('')
      setAdminSearch('')
    }

    const handleEditSave = async () => {
      if (!detailOrg) return
      setSaving(true)
      try {
        await updateAdminOrganizationApi(detailOrg.org_id, {
          org_name: editName || undefined,
          legal_entity_name: editLegalEntityName || undefined,
          vat_number: editVatNumber || undefined,
          registration_number: editRegistrationNumber || undefined,
          duns_number: editDunsNumber || undefined,
          registered_address: editRegisteredAddress || undefined,
        })
        setDetailOrg({
          ...detailOrg,
          org_name: editName,
          legal_entity_name: editLegalEntityName,
          vat_number: editVatNumber,
          registration_number: editRegistrationNumber,
          duns_number: editDunsNumber,
          registered_address: editRegisteredAddress,
        })
        setOrgs((prev) => prev.map((o) => o.org_id === detailOrg.org_id ? { ...o, org_name: editName } : o))
        setEditMode(false)
        toast.success('Organization updated successfully.')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update organization')
      } finally {
        setSaving(false)
      }
    }

    const handleVerifyChange = async () => {
      if (!detailOrg || !verifyStatus) return
      setVerifyLoading(true)
      setConfirmVerify(false)
      try {
        await updateOrgVerificationStatusApi(detailOrg.org_id, verifyStatus as 'pending' | 'active' | 'rejected' | 'inactive')
        setDetailOrg({ ...detailOrg, verified_status: verifyStatus })
        setOrgs((prev) => prev.map((o) => o.org_id === detailOrg.org_id ? { ...o, verified_status: verifyStatus } : o))
        toast.success(`Organization status set to '${verifyStatus}'.`)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update verification status')
      } finally {
        setVerifyLoading(false)
      }
    }

    // Loading state
    if (loading && orgs.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <div>
                <h1 className="text-2xl font-semibold">Organizations</h1>
                <p className="mt-1 text-sm text-muted-foreground">Manage all organizations on the platform.</p>
              </div>
            </div>
          </div>
          <FiltersBar
            searchInput={searchInput} setSearchInput={setSearchInput}
            onSearch={handleSearch} onKeyDown={handleKeyDown}
            onClear={clearFilters} hasFilters={hasFilters}
          />
          <SkeletonCard count={5} />
        </div>
      )
    }

    // Empty state
    if (orgs.length === 0 && !loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <div>
                <h1 className="text-2xl font-semibold">Organizations</h1>
                <p className="mt-1 text-sm text-muted-foreground">Manage all organizations on the platform.</p>
              </div>
            </div>
          </div>
          <FiltersBar
            searchInput={searchInput} setSearchInput={setSearchInput}
            onSearch={handleSearch} onKeyDown={handleKeyDown}
            onClear={clearFilters} hasFilters={hasFilters}
          />
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <BuildingIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No organizations found</p>
            <p className="text-sm text-muted-foreground">{hasFilters ? 'No organizations match the current filters.' : 'No organizations have been registered yet.'}</p>
            {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2 gap-1.5"><XIcon className="size-4" />Clear filters</Button>}
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Organizations</h1>
                <p className="mt-1 text-sm text-muted-foreground">Manage all organizations on the platform.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5 shrink-0">
                <FilterIcon className="size-4" />
                {showFilters ? 'Hide filters' : 'Filters'}
                {hasFilters && <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">!</span>}
              </Button>
            </div>
          </div>
        </div>

        <FiltersBar
          searchInput={searchInput} setSearchInput={setSearchInput}
          onSearch={handleSearch} onKeyDown={handleKeyDown}
          onClear={clearFilters} hasFilters={hasFilters}
        />

        <p className="mb-4 text-xs text-muted-foreground">
          {total} organization{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}
        </p>

        <div className="space-y-2">
          {orgs.map((org) => {
            const statusInfo = STATUS_STYLES[org.verified_status] ?? { label: org.verified_status, color: 'bg-muted text-muted-foreground' }
            return (
              <button key={org.org_id} type="button" onClick={() => openDetail(org.org_id)}
                className="w-full rounded-xl border border-border bg-card p-4 text-left transition-shadow hover:shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <BuildingIcon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-foreground">{org.org_name}</span>
                      <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                        <ShieldCheckIcon className="size-3" />
                        {statusInfo.label}
                      </span>
                      {org.tier && (
                        <TierPill tier={org.tier} />
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <UsersIcon className="size-3" />
                        {org.employee_count} employee{org.employee_count !== 1 ? 's' : ''}
                      </span>
                      {org.country_name && (
                        <span className="inline-flex items-center gap-1">
                          <GlobeIcon className="size-3" />
                          {org.country_name}
                        </span>
                      )}
                      {org.currency && (
                        <span className="inline-flex items-center gap-1">
                          <BanknoteIcon className="size-3" />
                          {org.currency}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <ClockIcon className="size-3" />
                        Created {formatDate(org.created_at)}
                      </span>
                    </div>
                  </div>
                  <EyeIcon className="mt-1 size-4 shrink-0 text-muted-foreground" />
                </div>
              </button>
            )
          })}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5">
              <ChevronLeftIcon className="size-4" />Previous
            </Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">
              Next<ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}

        {/* Detail Drawer */}
        {showDetail && (
          <>
            <button type="button" aria-label="Close detail" className="fixed inset-0 z-40 bg-black/30" onClick={closeDetail} />
            <aside aria-label="Organization details" className="fixed inset-y-0 right-0 z-50 w-full max-w-lg border-l border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200 overflow-y-auto">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold text-foreground">Organization details</h2>
                <button type="button" onClick={closeDetail} className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Close">
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="overflow-y-auto p-5" style={{ height: 'calc(100vh - 65px)' }}>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-16"><LoaderIcon className="size-5 animate-spin text-muted-foreground" /></div>
                ) : detailOrg ? (
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center gap-4">
                      <div className="flex size-14 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><BuildingIcon className="size-6" /></div>
                      <div className="min-w-0">
                        {editMode ? (
                          <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                            className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-lg font-semibold outline-none focus:border-primary" />
                        ) : (
                          <h3 className="truncate text-lg font-semibold text-foreground">{detailOrg.org_name}</h3>
                        )}
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${(STATUS_STYLES[detailOrg.verified_status] ?? { color: 'bg-muted text-muted-foreground' }).color}`}>
                          <ShieldCheckIcon className="size-3" />{STATUS_STYLES[detailOrg.verified_status]?.label ?? detailOrg.verified_status}
                        </span>
                      </div>
                    </div>

                    {/* Edit/Save buttons */}
                    <div className="flex gap-2">
                      {editMode ? (
                        <>
                          <Button size="sm" onClick={handleEditSave} disabled={saving} className="gap-1.5">
                            {saving ? <LoaderIcon className="size-4 animate-spin" /> : <CheckCircleIcon className="size-4" />}
                            Save
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditName(detailOrg.org_name); setEditLegalEntityName(detailOrg.legal_entity_name || ''); setEditVatNumber(detailOrg.vat_number || ''); setEditRegistrationNumber(detailOrg.registration_number || ''); setEditDunsNumber(detailOrg.duns_number || ''); setEditRegisteredAddress(detailOrg.registered_address || '') }}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setEditMode(true)} className="gap-1.5">
                          <FileEditIcon className="size-4" />Edit
                        </Button>
                      )}
                    </div>

                    {/* Fields */}
                    <div className="space-y-4">
                      {editMode ? (
                        <div className="space-y-3">
                          <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">Legal entity name</label>
                            <input type="text" value={editLegalEntityName} onChange={(e) => setEditLegalEntityName(e.target.value)}
                              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">VAT number</label>
                            <input type="text" value={editVatNumber} onChange={(e) => setEditVatNumber(e.target.value)}
                              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">Registration number</label>
                            <input type="text" value={editRegistrationNumber} onChange={(e) => setEditRegistrationNumber(e.target.value)}
                              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">D-U-N-S number</label>
                            <input type="text" value={editDunsNumber} onChange={(e) => setEditDunsNumber(e.target.value)} maxLength={9}
                              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-medium text-muted-foreground">Registered address</label>
                            <input type="text" value={editRegisteredAddress} onChange={(e) => setEditRegisteredAddress(e.target.value)}
                              className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary" />
                          </div>
                        </div>
                      ) : (
                        <>
                          <DetailField icon={<HashIcon className="size-4" />} label="Org ID" value={String(detailOrg.org_id)} />
                          <DetailField icon={<HashIcon className="size-4" />} label="VAT number" value={detailOrg.vat_number || '—'} />
                          <DetailField icon={<HashIcon className="size-4" />} label="D-U-N-S number" value={detailOrg.duns_number || '—'} />
                          <DetailField icon={<HashIcon className="size-4" />} label="Registration number" value={detailOrg.registration_number || '—'} />
                          <DetailField icon={<MapPinIcon className="size-4" />} label="Registered address" value={detailOrg.registered_address || '—'} />
                          <DetailField icon={<GlobeIcon className="size-4" />} label="Country" value={detailOrg.country_name || '—'} />
                          <DetailField icon={<BanknoteIcon className="size-4" />} label="Currency" value={detailOrg.currency || '—'} />
                          <DetailField icon={<UsersIcon className="size-4" />} label="Employees" value={String(detailOrg.employee_count)} />
                          <DetailField icon={<ClockIcon className="size-4" />} label="Created" value={formatTimestamp(detailOrg.created_at)} />
                        </>
                      )}
                    </div>

                    {/* Change verification status */}
                    <div className="border-t border-border pt-4">
                      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Verification status</h4>
                      <div className="flex gap-2">
                        <select value={verifyStatus} onChange={(e) => setVerifyStatus(e.target.value)}
                          className="flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="rejected">Rejected</option>
                          <option value="inactive">Inactive</option>
                        </select>
                        <Button size="sm" onClick={() => setConfirmVerify(true)} disabled={verifyLoading || verifyStatus === detailOrg.verified_status} className="gap-1.5">
                          {verifyLoading ? <LoaderIcon className="size-4 animate-spin" /> : <ShieldCheckIcon className="size-4" />}
                          Update
                        </Button>
                      </div>
                    </div>

                    {/* Subscription pricing override */}
                    {subscriptionData && (
                      <div className="border-t border-border pt-4">
                        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                          <TagIcon className="mr-1 inline size-3" />
                          Subscription pricing
                        </h4>
                        <div className="space-y-3">
                          {/* Plan default prices */}
                          <div className="rounded-lg bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                            Plan default:{' '}
                            <span className="font-medium text-foreground">
                              {subscriptionData.currency_symbol || ''}{(subscriptionData.plan_price_per_user_cents / 100).toFixed(2)}/user
                            </span>
                            {' · Min. '}
                            <span className="font-medium text-foreground">
                              {subscriptionData.currency_symbol || ''}{(subscriptionData.plan_minimum_monthly_cents / 100).toFixed(2)}/month
                            </span>
                            {subscriptionData.override_price_per_user_cents != null && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                                Override active
                              </span>
                            )}
                          </div>
                          {/* Override form */}
                          <div className="flex flex-wrap items-end gap-2">
                            <div className="flex-1 min-w-[120px]">
                              <label className="mb-0.5 block text-[10px] font-medium text-muted-foreground">
                                Per-user price (cents)
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={overridePrice}
                                onChange={(e) => setOverridePrice(e.target.value)}
                                placeholder={String(subscriptionData.plan_price_per_user_cents)}
                                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                              />
                            </div>
                            <div className="flex-1 min-w-[120px]">
                              <label className="mb-0.5 block text-[10px] font-medium text-muted-foreground">
                                Minimum monthly (cents)
                              </label>
                              <input
                                type="number"
                                min="0"
                                value={overrideMinimum}
                                onChange={(e) => setOverrideMinimum(e.target.value)}
                                placeholder={String(subscriptionData.plan_minimum_monthly_cents)}
                                className="w-full rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm outline-none focus:border-primary"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                setSavingOverride(true)
                                try {
                                  const priceVal = overridePrice.trim() !== '' ? parseInt(overridePrice, 10) : null
                                  const minVal = overrideMinimum.trim() !== '' ? parseInt(overrideMinimum, 10) : null
                                  await updateOrgSubscriptionApi(subscriptionData.org_id, {
                                    override_price_per_user_cents: priceVal,
                                    override_minimum_monthly_cents: minVal,
                                  })
                                  // Update local state to avoid extra API call
                                  setSubscriptionData((prev) => prev ? { ...prev, override_price_per_user_cents: priceVal, override_minimum_monthly_cents: minVal } : prev)
                                  toast.success(priceVal != null ? 'Pricing override saved.' : 'Pricing override cleared (using plan defaults).')
                                } catch (err) {
                                  toast.error(err instanceof Error ? err.message : 'Failed to save pricing override')
                                } finally {
                                  setSavingOverride(false)
                                }
                              }}
                              disabled={savingOverride}
                              className="gap-1.5"
                            >
                              {savingOverride ? <LoaderIcon className="size-4 animate-spin" /> : <CheckCircleIcon className="size-4" />}
                              Apply
                            </Button>
                            {subscriptionData.override_price_per_user_cents != null && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  setSavingOverride(true)
                                  try {
                                    await updateOrgSubscriptionApi(subscriptionData.org_id, {
                                      override_price_per_user_cents: null,
                                      override_minimum_monthly_cents: null,
                                    })
                                    // Update local state
                                    setSubscriptionData((prev) => prev ? { ...prev, override_price_per_user_cents: null, override_minimum_monthly_cents: null } : prev)
                                    setOverridePrice('')
                                    setOverrideMinimum('')
                                    toast.success('Pricing override cleared. Using plan defaults.')
                                  } catch (err) {
                                    toast.error(err instanceof Error ? err.message : 'Failed to clear pricing override')
                                  } finally {
                                    setSavingOverride(false)
                                  }
                                }}
                                disabled={savingOverride}
                                className="gap-1.5"
                              >
                                <XCircleIcon className="size-4" />
                                Clear
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Admins */}
                    {detailOrg.admins.length > 0 && (() => {
                      const q = adminSearch.trim().toLowerCase()
                      const filteredAdmins = q
                        ? detailOrg.admins.filter((a) =>
                            `${a.first_name} ${a.last_name}`.toLowerCase().includes(q) ||
                            a.email.toLowerCase().includes(q),
                          )
                        : detailOrg.admins

                      return (
                        <div className="border-t border-border pt-4">
                          <div className="mb-3 flex items-center justify-between">
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Admins</h4>
                            {q && (
                              <span className="text-[10px] text-muted-foreground">
                                {filteredAdmins.length} of {detailOrg.admins.length}
                              </span>
                            )}
                          </div>
                          {detailOrg.admins.length > 3 && (
                            <div className="relative mb-3">
                              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                              <input
                                type="text"
                                value={adminSearch}
                                onChange={(e) => setAdminSearch(e.target.value)}
                                placeholder="Filter admins by name or email…"
                                className="w-full rounded-lg border border-border bg-background py-1.5 pl-8 pr-3 text-xs outline-none focus:border-primary"
                              />
                            </div>
                          )}
                          <div className="space-y-2">
                            {filteredAdmins.length === 0 ? (
                              <p className="py-3 text-center text-xs text-muted-foreground">
                                No admins match "{adminSearch}".
                              </p>
                            ) : (
                              filteredAdmins.map((admin) => (
                                <div key={admin.user_id} className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2">
                                  <div className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                                    {admin.first_name.charAt(0)}{admin.last_name.charAt(0)}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-foreground">{admin.first_name} {admin.last_name}</p>
                                    <p className="text-xs text-muted-foreground">{admin.email}</p>
                                  </div>
                                  <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    {admin.role.replace(/_/g, ' ')}
                                  </span>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )
                    })()}

                    {/* Circle stats */}
                    <div className="border-t border-border pt-4">
                      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Circle Statistics</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <CircleStat label="Total" value={String(detailOrg.circles.total_circles)} />
                        <CircleStat label="Open" value={String(detailOrg.circles.open_circles)} />
                        <CircleStat label="Completed" value={String(detailOrg.circles.completed_circles)} />
                        <CircleStat label="Cancelled" value={String(detailOrg.circles.cancelled_circles)} />
                      </div>
                    </div>

                    {/* Cycle stats */}
                    <div className="border-t border-border pt-4">
                      <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Cycle Statistics</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <CircleStat label="Total" value={String(detailOrg.cycles.total_cycles)} />
                        <CircleStat label="Pending" value={String(detailOrg.cycles.pending_cycles)} />
                        <CircleStat label="Paid" value={String(detailOrg.cycles.paid_cycles)} />
                        <CircleStat label="Overdue" value={String(detailOrg.cycles.overdue_cycles)} />
                      </div>
                    </div>

                    {/* ── Dissolve Org button ── */}
                    <div className="border-t border-border pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDissolve(true)}
                        disabled={dissolving}
                        className="w-full gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
                      >
                        {dissolving ? (
                          <LoaderIcon className="size-4 animate-spin" />
                        ) : (
                          <Trash2Icon className="size-4" />
                        )}
                        Dissolve organization
                      </Button>
                      <p className="mt-1.5 text-[11px] text-muted-foreground text-center">
                        Sets org to inactive, orphans users, cancels active Circles.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="py-16 text-center text-sm text-muted-foreground">Could not load organization details.</p>
                )}
              </div>
            </aside>

            {/* Verify confirmation dialog */}
            <Dialog open={confirmVerify} onClose={() => setConfirmVerify(false)}
              title="Change verification status?"
              description={`This will change the verification status of "${detailOrg?.org_name}" to "${verifyStatus}".`}
              footer={
                <>
                  <Button variant="outline" size="sm" onClick={() => setConfirmVerify(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleVerifyChange} className="gap-1.5">
                    <ShieldCheckIcon className="size-4" />Confirm
                  </Button>
                </>
              }
            />

            {/* ── Dissolve confirmation dialog ── */}
            <DestructiveConfirm
              open={confirmDissolve}
              onClose={() => setConfirmDissolve(false)}
              title="Dissolve organization?"
              description={`This will set "${detailOrg?.org_name}" to inactive, orphan all employees from the org, and cancel all active Funding Circles. This action cannot be undone. Audit trails are preserved.`}
              confirmLabel="Yes, dissolve"
              onConfirm={async () => {
                if (!detailOrg) return
                setDissolving(true)
                setConfirmDissolve(false)
                try {
                  await deleteAdminOrganizationApi(detailOrg.org_id)
                  toast.success(`"${detailOrg.org_name}" has been dissolved.`)
                  setOrgs((prev) => prev.filter((o) => o.org_id !== detailOrg.org_id))
                  setTotal((prev) => Math.max(0, prev - 1))
                  closeDetail()
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Failed to dissolve organization')
                } finally {
                  setDissolving(false)
                }
              }}
              loading={dissolving}
            />
          </>
        )}
      </div>
    )
  },
})

function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  )
}

function CircleStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-muted/50 px-3 py-2">
      <p className="text-[10px] font-medium text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold tabular-nums">{value}</p>
    </div>
  )
}

function TierPill({ tier }: { tier: 'growth' | 'enterprise' }) {
  const config = {
    growth: { icon: TrendingUpIcon, label: 'Growth', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    enterprise: { icon: ShieldCheckIcon, label: 'Enterprise', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  }
  const c = config[tier] || config.growth
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.color}`}>
      <c.icon className="size-3" />
      {c.label}
    </span>
  )
}

function FiltersBar({
  searchInput, setSearchInput,
  onSearch, onKeyDown, onClear, hasFilters,
}: {
  searchInput: string; setSearchInput: (v: string) => void
  onSearch: () => void; onKeyDown: (e: React.KeyboardEvent) => void
  onClear: () => void; hasFilters: boolean
}) {
  return (
    <div className="mb-6 space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={onKeyDown}
            autoFocus
            placeholder="Search by organization name…" className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm outline-none focus:border-primary" />
        </div>
        <Button size="sm" onClick={onSearch} className="gap-1.5"><SearchIcon className="size-4" />Search</Button>
        {hasFilters && <Button variant="outline" size="sm" onClick={onClear} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
      </div>
    </div>
  )
}
